/**
 * axon ROUTER
 * Routes between all axon forms based on sidebar selection
 * Integrates with Sidebar component
 * @version 1.2.0 - Keeps only recently used forms mounted to reduce memory pressure
 */

import React, { useState, useEffect } from 'react';
import { AmbientForm, DatabankForm, MasterSheetForm, BDDetailsForm, MeterReadingForm } from './forms_pages';
import { axon_CONFIG, axonFormType } from './config/siteConfig';

interface axonRouterProps {
  activeForm: string; // Comes from Sidebar state
  site?: string; // Site code from URL
}

export const AxonRouter: React.FC<axonRouterProps> = ({ activeForm, site }) => {
  const MAX_MOUNTED_FORMS = 2;

  // Track recently mounted forms (LRU style)
  const [mountedForms, setMountedForms] = useState<string[]>([activeForm]);

  // Add active form and keep only the most recently used forms mounted.
  useEffect(() => {
    setMountedForms(prev => {
      const withoutActive = prev.filter(form => form !== activeForm);
      const updated = [...withoutActive, activeForm];
      if (updated.length <= MAX_MOUNTED_FORMS) {
        return updated;
      }
      return updated.slice(updated.length - MAX_MOUNTED_FORMS);
    });
  }, [activeForm]);

  // Render only recent mounted forms but show one active.
  return (
    <>
      {mountedForms.includes('Daily Report') && (
        <div className="w-full h-full" style={{ display: activeForm === 'Daily Report' ? 'flex' : 'none', flexDirection: 'column' }}>
          <MasterSheetForm key={`master-${site}`} site={site} />
        </div>
      )}
      {mountedForms.includes('Weather') && (
        <div className="w-full h-full" style={{ display: activeForm === 'Weather' ? 'flex' : 'none', flexDirection: 'column' }}>
          <AmbientForm key={`ambient-${site}`} site={site} />
        </div>
      )}
      {mountedForms.includes('Inverters') && (
        <div className="w-full h-full" style={{ display: activeForm === 'Inverters' ? 'flex' : 'none', flexDirection: 'column' }}>
          <DatabankForm key={`databank-${site}`} site={site} />
        </div>
      )}
      {mountedForms.includes('Breakdowns') && (
        <div className="w-full h-full" style={{ display: activeForm === 'Breakdowns' ? 'flex' : 'none', flexDirection: 'column' }}>
          <BDDetailsForm key={`bd-${site}`} site={site} />
        </div>
      )}
      {mountedForms.includes('Plant Meters') && (
        <div className="w-full h-full" style={{ display: activeForm === 'Plant Meters' ? 'flex' : 'none', flexDirection: 'column' }}>
          <MeterReadingForm key={`meter-${site}`} site={site} />
        </div>
      )}
    </>
  );
};
